<template>
  <article
    :draggable="!isDragDisabled"
    class="group block border border-slate-200 bg-white px-3 py-2 text-left shadow-sm transition focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:border-slate-800 dark:bg-slate-800 dark:shadow-black/20"
    :class="interactionClass"
    :title="isLockedByOther ? lockTitle : null"
    role="button"
    tabindex="0"
    @click="openTask"
    @dragstart="handleDragstart"
    @keydown.enter.prevent="openTask"
    @keydown.space.prevent="openTask"
  >
    <div class="flex items-start gap-2">
      <a
        :href="`/tasks/${task.id}`"
        class="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center text-indigo-700 outline-none hover:bg-indigo-50 hover:text-indigo-900 focus:ring-2 focus:ring-indigo-500 dark:text-indigo-400 dark:hover:bg-indigo-500/10 dark:hover:text-indigo-300"
        aria-label="Focus"
        title="Focus"
        @click.stop
        @keydown.enter.stop
        @keydown.space.stop
      >
        <svg class="h-3.5 w-3.5" viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.7" aria-hidden="true">
          <circle cx="10" cy="10" r="5.5" />
          <path d="M10 2.5v3M10 14.5v3M2.5 10h3M14.5 10h3" stroke-linecap="round" />
          <circle cx="10" cy="10" r="1.4" fill="currentColor" stroke="none" />
        </svg>
      </a>
      <span :class="handleClass" aria-hidden="true">::</span>
      <div class="min-w-0 flex-1">
        <p class="flex min-w-0 items-center gap-1.5 text-sm font-medium text-slate-950 dark:text-slate-100">
          <span class="min-w-0 truncate">{{ task.title }}</span>
          <span
            v-if="task.locked"
            class="inline-flex shrink-0 text-amber-600"
            :title="lockTitle"
            :aria-label="lockTitle"
          >
            <svg class="h-3.5 w-3.5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
              <path
                fill-rule="evenodd"
                d="M5 8V6a5 5 0 0 1 10 0v2h1a1 1 0 0 1 1 1v8a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V9a1 1 0 0 1 1-1h1Zm2 0h6V6a3 3 0 0 0-6 0v2Z"
                clip-rule="evenodd"
              />
            </svg>
          </span>
        </p>
        <div v-if="hasMeta" class="mt-1 flex flex-wrap items-center gap-1.5 text-[11px] leading-4 text-slate-500 dark:text-slate-400">
          <span v-if="task.ref" class="border border-slate-200 bg-slate-50 px-1.5 py-0.5 font-medium text-slate-700 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200">
            {{ task.ref }}
          </span>
          <span v-if="task.planning_state" class="border border-slate-200 px-1.5 py-0.5 dark:border-slate-700">
            {{ planningStateLabel }}
          </span>
          <span v-if="task.project" class="max-w-full truncate border border-slate-200 bg-slate-50 px-1.5 py-0.5 text-slate-600 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300">
            {{ task.project.name }}
          </span>
          <span v-if="task.effort" class="inline-flex items-center gap-1 border border-slate-200 px-1.5 py-0.5 dark:border-slate-700">
            <svg class="h-3 w-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <path d="m15 12-8.373 8.373a1 1 0 1 1-3-3L12 9"/>
              <path d="m18 15 4-4"/>
              <path d="m21.5 11.5-1.914-1.914A2 2 0 0 1 19 8.172V7l-2.26-2.26a6 6 0 0 0-4.202-1.756L9 2.96l.92.82A6.18 6.18 0 0 1 12 8.4V10l2 2h1.172a2 2 0 0 1 1.414.586L18.5 14.5"/>
            </svg>
            {{ formattedEffort }}
          </span>
          <span
            v-for="tag in visibleTags"
            :key="tag"
            class="border border-slate-200 bg-slate-50 px-1.5 py-0.5 text-slate-500 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-400"
          >
            {{ tag }}
          </span>
        </div>
      </div>
    </div>
  </article>
</template>

<script setup>
import { computed } from 'vue'
import { visibleTaskTags } from './tags'

const props = defineProps({
  store: {
    type: Object,
    required: true,
  },
  task: {
    type: Object,
    required: true,
  },
  heldToken: {
    type: String,
    default: null,
  },
})

const emit = defineEmits(['dragstart'])

const formattedEffort = computed(() => `${Number(props.task.effort).toLocaleString()}h`)
const visibleTags = computed(() => visibleTaskTags(props.task.tags))
const planningStateLabel = computed(() => {
  if (props.task.planning_state !== 'parked') return formatLabel(props.task.planning_state)

  return props.task.gut_score == null ? '💤' : `💤 ${props.task.gut_score}/10`
})
const hasMeta = computed(() => Boolean(
  props.task.ref ||
    props.task.planning_state ||
    props.task.project ||
    props.task.effort ||
    visibleTags.value.length
))
const isLockedByOther = computed(() => Boolean(props.task.locked && !props.heldToken))
const isDragDisabled = computed(() => isLockedByOther.value || Boolean(props.store.projectFilterId.value))
const interactionClass = computed(() => {
  if (isLockedByOther.value) return 'cursor-not-allowed opacity-60'
  if (props.store.projectFilterId.value) return 'cursor-pointer hover:border-slate-300 hover:bg-slate-50 dark:hover:border-slate-700 dark:hover:bg-slate-800/80'

  return 'cursor-grab hover:border-slate-300 hover:bg-slate-50 active:cursor-grabbing dark:hover:border-slate-700 dark:hover:bg-slate-800/80'
})
const handleClass = computed(() => {
  if (props.store.projectFilterId.value) return 'mt-1 text-slate-200 dark:text-slate-700'

  return 'mt-1 text-slate-300 group-hover:text-slate-400 dark:text-slate-600 dark:group-hover:text-slate-500'
})
const lockTitle = computed(() => {
  const label = props.task.locked_by_label || 'another user'
  const via = props.task.locked_via ? ` via ${props.task.locked_via}` : ''
  return `Locked by ${label}${via}`
})

function openTask() {
  if (isLockedByOther.value) return

  props.store.openTask(props.task.id)
}

function handleDragstart(event) {
  if (isDragDisabled.value) {
    event.preventDefault()
    return
  }

  emit('dragstart', props.task)
}

function formatLabel(value) {
  return (value || '').replaceAll('_', ' ').replace(/\b\w/g, (letter) => letter.toUpperCase())
}
</script>
